import { Button, Form, message, Steps } from 'antd';
import { useTranslation } from '../../i18n/i18n';
import React, { useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import UserForm from './UserForm';
import './NewUserModal.scss';
import ModalCustom from '../utils/ModalCustom/ModalCustom';
import { userApi } from '../../api/user.api';
import useAuthStore from '../../stores/auth.store';
import useTenantStore, { ALL_TENANTS_VALUE } from '../../stores/tenant.store';
import { UserRole } from '../../gen/proto/api/gateway/v1/user_pb';
import TempPasswordDisplay from './TempPasswordDisplay';

interface NewUserModalProps {
  onUserUpdated?: () => void;
}

const NewUserModal = ({ onUserUpdated }: NewUserModalProps) => {
  const { t } = useTranslation('users', { keyPrefix: 'modal' });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingCreateUser, setLoadingCreateUser] = useState<boolean>(false);
  const [form] = Form.useForm();
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);

  const itemsStep = [{ title: t('user') }, { title: t('password') }];

  useEffect(() => {
    if (!isModalOpen) {
      setCurrentStep(0);
    }
  }, [isModalOpen]);

  const createUserButton = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      return;
    }

    try {
      let role = values.role;
      if (!isSuperAdmin) {
        role = UserRole.USER;
      }
      const tenantUuid = selectedTenant === ALL_TENANTS_VALUE ? values.tenant : selectedTenant;
      setLoadingCreateUser(true);
      const response = await userApi.createUser(values.email, values.name, role, tenantUuid);
      setTempPassword(response.temporaryPassword);
      setCurrentStep(1);
    } catch (err) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setLoadingCreateUser(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Button type={'primary'} onClick={() => setIsModalOpen(true)}>
        <PlusOutlined />
        {t('newUser')}
      </Button>
      <ModalCustom
        title={t('newUser')}
        description={t('desc')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={
          currentStep === 0 ? (
            <Button onClick={createUserButton} type={'primary'} loading={loadingCreateUser}>
              {t('next')}
            </Button>
          ) : (
            <Button
              type={'primary'}
              onClick={() => {
                setCurrentStep(0);
                onUserUpdated?.();
                setIsModalOpen(false);
              }}
            >
              {t('save')}
            </Button>
          )
        }
      >
        <Steps className={'Modal__steps'} current={currentStep} items={itemsStep} />
        {currentStep === 0 && <UserForm form={form} />}
        {currentStep === 1 && <TempPasswordDisplay password={tempPassword} />}
      </ModalCustom>
    </>
  );
};

export default NewUserModal;
